import { FC, ReactNode } from 'react';

import { AxiosCartError, CartError } from '@telialt/models/src/EshopModel';
import { RequestStatus } from '@telialt/models/src/RequestModel';
import { selectIsCartAnonymous, selectProductInCart, selectProductInCartCode } from '@telialt/utils/src/eshopSelectors';
import { setPreviousProduct, updateCartSession, updateStartOverLink } from '@telialt/utils/src/eshopUtils';
import { getIsUserAuthorized } from '@telialt/utils/src/SSO/utils';

import { useCloneCartMutation } from '../hooks/mutations/useCloneCartMutation';
import { useMigrateCartMutation } from '../hooks/mutations/useMigrateCartMutation';
import { useStrategyLoadCartQuery } from '../hooks/queries/useStrategyLoadCartQuery';

type Props = {
    loadingPlaceholder: ReactNode;
    ErrorComponent?: FC<{ error: AxiosCartError | CartError | Error }>;
    children?: ReactNode;
};
/**
 * ## not to import directly, only by <CartInitializer.StrategyLoad
 *
 *  @see CartInitializer.StrategyLoad
 *  */
export const StrategyLoad: FC<Props> = ({ children, loadingPlaceholder, ErrorComponent }) => {
    const { migrateCart, isErrorMigrateCart, isLoadingMigrateCart, statusMigrateCart, migrateCartError } =
        useMigrateCartMutation();

    const { cloneCart, isErrorCloneCart, isLoadingCloneCart, statusCloneCart, cloneCartError } = useCloneCartMutation();

    const {
        data: cart,
        isError: isErrorLoadCart,
        status: statusLoadCart,
        error: errorLoadCart,
    } = useStrategyLoadCartQuery();

    if (cart) {
        updateCartSession(cart);
        updateStartOverLink(cart);
        const productCode = selectProductInCart(cart)?.baseProduct || selectProductInCartCode(cart);
        setPreviousProduct(productCode);
    }

    if (cart?.generated && !isLoadingCloneCart) {
        cloneCart({ cartId: cart.guid });
    }

    const isMigratable = selectIsCartAnonymous(cart) && getIsUserAuthorized();

    if (!isLoadingMigrateCart && cart?.generated === false && isMigratable) {
        migrateCart({ oldCartId: cart.guid });
    }

    const isError = isErrorLoadCart || isErrorCloneCart || isErrorMigrateCart;

    if (isError) {
        return ErrorComponent ? (
            <ErrorComponent error={errorLoadCart || cloneCartError || migrateCartError} />
        ) : (
            <>{loadingPlaceholder}</>
        );
    }

    const isLoading =
        statusLoadCart === RequestStatus.Pending ||
        (cart?.generated && statusCloneCart === RequestStatus.Idle) ||
        isLoadingCloneCart ||
        (isMigratable && statusMigrateCart === RequestStatus.Idle) ||
        isLoadingMigrateCart;

    if (isLoading) {
        return <>{loadingPlaceholder}</>;
    }

    return <>{children}</>;
};
